import React from 'react';
import { Box, Typography, Paper, Button, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';

interface InvoiceStatusPipelineProps {
  currentStatus: string;
  onStatusChange?: (status: string) => void;
  fillHeight?: boolean;
}

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.5; }
  70%  { transform: scale(1.55); opacity: 0; }
  100% { transform: scale(1.55); opacity: 0; }
`;

const STATIONS = ['Entwurf', 'Versendet', 'Bezahlt'] as const;
const CIRCLE_SIZE = 42;
const TRACK_HEIGHT = 6;

const InvoiceStatusPipeline: React.FC<InvoiceStatusPipelineProps> = ({
  currentStatus,
  onStatusChange,
  fillHeight = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const isStorniert = currentStatus === 'Storniert';
  const currentIndex = isStorniert ? -1 : STATIONS.indexOf(currentStatus as typeof STATIONS[number]);

  const accent = isDark ? '#3dd47e' : '#2ba862';
  const accentFaded = isDark ? 'rgba(61,212,126,0.15)' : 'rgba(43,168,98,0.10)';
  const trackBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const futureBorder = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
  const futureBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)';
  const redAccent = '#f44336';

  const tubeTop = (CIRCLE_SIZE - TRACK_HEIGHT) / 2;
  const fillWidthPct = isStorniert ? 0 : currentIndex <= 0 ? 0 : (currentIndex / (STATIONS.length - 1)) * 100;

  const statusLabel = isStorniert ? 'Storniert' : (STATIONS[currentIndex] ?? 'Unbekannt');

  const canAdvance = (stationIndex: number): boolean => {
    if (!onStatusChange || isStorniert) return false;
    return stationIndex === currentIndex + 1;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        px: 3,
        pt: fillHeight ? 1.5 : 2.5,
        pb: fillHeight ? 1.5 : 3,
        mb: 2,
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        ...(fillHeight && {
          height: '100%',
          marginBottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          minHeight: 0,
        }),
      }}
    >
      {/* Header */}
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ fontWeight: 600, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', mb: 0.25 }}
      >
        Rechnungs-Status
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: fillHeight ? 1.25 : 3, fontSize: '0.82rem' }}>
        Aktueller Status:{' '}
        <Box component="span" sx={{ color: isStorniert ? redAccent : accent, fontWeight: 600 }}>
          {statusLabel}
        </Box>
      </Typography>

      {/* Pipeline container */}
      <Box sx={{ ...(fillHeight && { flex: 1, width: '100%', display: 'flex', alignItems: 'center' }) }}>
        <Box sx={{ position: 'relative', width: '100%' }}>
          {/* Gray track */}
          <Box sx={{ position: 'absolute', top: tubeTop, left: 0, width: '100%', height: TRACK_HEIGHT, borderRadius: TRACK_HEIGHT / 2, bgcolor: trackBg }} />

          {/* Green fill */}
          {fillWidthPct > 0 && (
            <Box
              sx={{
                position: 'absolute', top: tubeTop, left: 0,
                width: `${fillWidthPct}%`, height: TRACK_HEIGHT,
                borderRadius: TRACK_HEIGHT / 2,
                background: `linear-gradient(90deg, ${accent}, ${accent}cc)`,
                transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          )}

          {/* Stations */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
            {STATIONS.map((station, index) => {
              const isCompleted = !isStorniert && currentIndex >= 0 && index < currentIndex;
              const isCurrent = !isStorniert && index === currentIndex;
              const isFuture = isStorniert || currentIndex < 0 || index > currentIndex;
              const clickable = canAdvance(index);

              return (
                <Box key={station} sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box
                    onClick={clickable ? () => onStatusChange!(station) : undefined}
                    sx={{
                      position: 'relative', width: CIRCLE_SIZE, height: CIRCLE_SIZE,
                      ...(clickable && {
                        cursor: 'pointer',
                        '&:hover > :last-child': { transform: 'scale(1.12)', boxShadow: `0 0 16px ${accent}44` },
                      }),
                    }}
                  >
                    {isCurrent && (
                      <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `2px solid ${accent}`, animation: `${pulseRing} 2s ease-out infinite` }} />
                    )}

                    <Box
                      sx={{
                        width: '100%', height: '100%', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.4s ease',
                        ...(isCompleted && { bgcolor: accent, border: `2px solid ${accent}` }),
                        ...(isCurrent && { bgcolor: accent, border: `2px solid ${accent}`, boxShadow: `0 0 14px ${accent}55` }),
                        ...(isFuture && { bgcolor: futureBg, border: `2px dashed ${futureBorder}` }),
                        ...(clickable && { border: `2px dashed ${accent}`, bgcolor: accentFaded }),
                      }}
                    >
                      {isCompleted && <CheckIcon sx={{ fontSize: 20, color: '#fff' }} />}
                      {isCurrent && <CheckIcon sx={{ fontSize: 22, color: '#fff' }} />}
                      {isFuture && !clickable && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: futureBorder }} />}
                      {clickable && <CheckIcon sx={{ fontSize: 18, color: accent, opacity: 0.6 }} />}
                    </Box>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1, fontWeight: isCurrent ? 700 : isCompleted ? 600 : 400,
                      fontSize: '0.72rem',
                      color: isCurrent ? accent : isCompleted ? 'text.primary' : clickable ? accent : 'text.disabled',
                      textAlign: 'center', lineHeight: 1.2, maxWidth: 100,
                      ...(isCurrent && { bgcolor: accentFaded, px: 1, py: 0.25, borderRadius: 1 }),
                    }}
                  >
                    {station}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Storniert indicator / button */}
      <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {isStorniert ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: isDark ? 'rgba(244,67,54,0.12)' : 'rgba(244,67,54,0.08)', border: `1px solid ${isDark ? 'rgba(244,67,54,0.3)' : 'rgba(244,67,54,0.2)'}` }}>
            <BlockIcon sx={{ fontSize: 18, color: redAccent }} />
            <Typography variant="body2" sx={{ fontWeight: 700, color: redAccent, fontSize: '0.82rem' }}>
              Diese Rechnung wurde storniert.
            </Typography>
          </Box>
        ) : onStatusChange && (currentStatus === 'Entwurf' || currentStatus === 'Versendet') ? (
          <Button
            size="small" variant="outlined"
            startIcon={<BlockIcon sx={{ fontSize: 16 }} />}
            onClick={() => onStatusChange('Storniert')}
            sx={{
              color: isDark ? 'rgba(244,67,54,0.7)' : 'rgba(244,67,54,0.6)',
              borderColor: isDark ? 'rgba(244,67,54,0.3)' : 'rgba(244,67,54,0.25)',
              fontSize: '0.75rem', textTransform: 'none', fontWeight: 500,
              '&:hover': { borderColor: redAccent, color: redAccent, bgcolor: isDark ? 'rgba(244,67,54,0.08)' : 'rgba(244,67,54,0.04)' },
            }}
          >
            Stornieren
          </Button>
        ) : null}
      </Box>
    </Paper>
  );
};

export default InvoiceStatusPipeline;
